// Однолінійна схема станції — перший вигляд (огляд). ТЗ SLD від 21.10.2026.
//
// КОМПОНУВАННЯ: згори точка приєднання (PCC) і легенда; ряд 1 — блоки УЗЕ на
// всю ширину; ряд 2 — блоки КТП. Лінії від PCC до кожного блока йдуть каскадом:
// шина над кожним рядом, між рядами — стояк у лівому полі, тож лінії ніде не
// перетинають плиток. На вузькому екрані ряди переносяться (2×2 УЗЕ тощо), і
// розводка будується так само — над кожним підрядом своя шина.
//
// ЗВІДКИ ВСЕ. Топологія — конфіг станції (src/scada/stations: таблиця адресації
// + «ТЗ для EMS box»), підписи з креслення — там само. У розмітці немає жодної
// назви чи адреси обладнання: інша станція = інший конфіг.
//
// ПРАВИЛО ТЗ: статика (модель, S/N, IP, Unit ID, привʼязки) — на плитці; у
// спливаючому вікні — лише значення, що змінюються. Нічого не дублюється.
// Немає телеметрії — «—» / «немає даних» і вік останніх даних, без демо-цифр.
//
// Живі значення — ті самі ендпоінти, що на вкладках «Інвертори» й «УЗЕ»:
// /scada/inverters/:id/latest (інвертор за Unit ID) і /scada/bess/:id/latest
// (PCS за назвою T1.1…, банк за номером x.GB).
import { cx } from '@/i18n/cfgText';
import { nfMk } from '@/scada/live';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { scada } from '@/scada/tokens';
import { useInverterLatest, type InvLatest } from '@/scada/useInverters';
import { useBessLatest, type BessUnit, type BessBattery } from '@/scada/useBess';
import type {
  StationTopology, StationDrawing, TopoBess, TopoBank, TopoPcs, TopoKtp, TopoGroup, TopoInverter,
} from '@/scada/stations/sldTypes';

// ─── стани (правила ті самі, що й у попередній схемі) ─────────────────────────

type NodeState = 'gen' | 'idle' | 'warn' | 'alarm' | 'stale' | 'nodata';
/** Чотири категорії легенди з ТЗ: Норма / Попередження / Аварія / Немає звʼязку. */
type Cat = 'ok' | 'warn' | 'alarm' | 'nodata';

const CAT_OF: Record<NodeState, Cat> = { gen: 'ok', idle: 'ok', warn: 'warn', stale: 'warn', alarm: 'alarm', nodata: 'nodata' };
const CAT_META: Record<Cat, { label: string; color: string }> = {
  ok: { get label() { return i18n.t('ak_sld_normal'); }, color: scada.state.ok },
  warn: { get label() { return i18n.t('ak_sld_warning'); }, color: scada.state.warning },
  alarm: { get label() { return i18n.t('ak_sld_alarm'); }, color: scada.state.alarm },
  nodata: { get label() { return i18n.t('ak_sld_no_link'); }, color: scada.state.offline },
};
const INV_LABEL: Record<NodeState, string> = {
  get gen() { return i18n.t('ak_sld_generating'); }, get idle() { return i18n.t('ak_sld_online_not_generating'); }, get warn() { return i18n.t('ak_sld_warning'); },
  get alarm() { return i18n.t('ak_sld_alarm'); }, get stale() { return i18n.t('ak_sld_temporary_link_loss'); }, get nodata() { return i18n.t('ak_sld_no_link'); },
};
const PCS_LABEL: Record<NodeState, string> = {
  get gen() { return i18n.t('ak_sld_running'); }, get idle() { return i18n.t('ak_sld_online'); },
  get warn() { return INV_LABEL.warn; }, get alarm() { return INV_LABEL.alarm; }, get stale() { return INV_LABEL.stale; }, get nodata() { return INV_LABEL.nodata; },
};

function worst(states: NodeState[]): NodeState {
  const order: NodeState[] = ['alarm', 'warn', 'stale', 'nodata', 'gen', 'idle'];
  if (states.length === 0) return 'nodata';
  if (states.every((s) => s === 'nodata')) return 'nodata';
  for (const s of order) if (states.includes(s)) return s === 'nodata' ? 'warn' : s;
  return 'idle';
}

/** Вік знімка, хв. readAtLocal — київський час із суфіксом «Z» (це не UTC). */
function ageMin(readAtLocal?: string | null): number | null {
  if (!readAtLocal) return null;
  const nowKyiv = Date.parse(new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Kyiv' }).replace(' ', 'T') + 'Z');
  const t = Date.parse(readAtLocal);
  if (!isFinite(t) || !isFinite(nowKyiv)) return null;
  return Math.max(0, (nowKyiv - t) / 60000);
}
function freshness(readAtLocal?: string | null): 'fresh' | 'stale' | 'nodata' {
  const a = ageMin(readAtLocal);
  if (a == null || a > 30) return 'nodata';
  if (a > 5) return 'stale';
  return 'fresh';
}

const ALARM_RE = /fault|alarm|error|аварі/i;
const IDLE_RE = /standby|wait|idle|sleep|stop|off/i;

/**
 * «Ніч» для станції: серед інверторів зі свіжими даними генерують менше 10 %.
 * Уночі колектор GoodWe позначає якість даних «idle», а самі інвертори Лучок
 * повідомляють стан «warning» (так усі 64 вночі 21.10). Це нічний режим, а не
 * відхилення: «idle» без потужності вночі — норма, крім аварії (fault/alarm/
 * error перевіряється раніше). Удень, коли станція генерує, той самий «idle»
 * лишається попередженням: інвертор стоїть, а сусіди працюють.
 */
const NIGHT_SHARE = 0.1;
function isNight(all: InvLatest[] | null | undefined): boolean {
  const fresh = (all ?? []).filter((l) => freshness(l.readAtLocal) === 'fresh');
  if (!fresh.length) return false;
  return fresh.filter((l) => (l.pacKw ?? 0) > 0.5).length / fresh.length < NIGHT_SHARE;
}
const nightIdle = (live: InvLatest, night: boolean) =>
  night && live.quality === 'idle' && (live.pacKw ?? 0) <= 0.5;

function invState(live: InvLatest | undefined, night = false): NodeState {
  if (!live) return 'nodata';
  const f = freshness(live.readAtLocal);
  if (f === 'nodata') return 'nodata';
  if (f === 'stale') return 'stale';
  if (ALARM_RE.test(live.status ?? '')) return 'alarm';
  if (nightIdle(live, night)) return 'idle';
  if (live.quality && live.quality !== 'ok') return 'warn';
  if ((live.pacKw ?? 0) > 0.5) return 'gen';
  if (live.status === 'ok' || IDLE_RE.test(live.status ?? '')) return 'idle';
  return 'warn';
}

type PcsLive = BessUnit & { readAtLocal?: string };
function pcsState(u: PcsLive | undefined): NodeState {
  if (!u) return 'nodata';
  const f = freshness(u.readAtLocal);
  if (f === 'nodata') return 'nodata';
  if (f === 'stale') return 'stale';
  if (u.alarmActive || ALARM_RE.test(u.status ?? '')) return 'alarm';
  if (u.quality && u.quality !== 'ok') return 'warn';
  if (Math.abs(u.powerKw ?? 0) > 0.5) return 'gen';
  return 'idle';
}

// ─── форматування ────────────────────────────────────────────────────────────

const nf0 = nfMk({ maximumFractionDigits: 0 });
const nf1 = nfMk({ minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nf2 = nfMk({ minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ok = (v: number | null | undefined): v is number => v != null && Number.isFinite(v);
const kw = (v: number | null | undefined) => (ok(v) ? nf0.format(v) : '—');
const mwOrKw = (v: number) => (Math.abs(v) >= 1000 ? `${nf2.format(v / 1000)} ${i18n.t('ak_sld_mw')}` : `${nf0.format(v)} ${i18n.t('ak_sld_kw')}`);
const clock = (s?: string | null) => (s ? s.slice(11, 16) : '—');
const age = (s?: string | null) => {
  const a = ageMin(s);
  if (a == null) return i18n.t('ak_sld_no_data');
  if (a < 1) return i18n.t('ak_sld_just_now');
  if (a < 60) return i18n.t('ak_sld_min_ago', { n: Math.round(a) });
  return i18n.t('ak_sld_h_min_ago', { h: Math.floor(a / 60), m: Math.round(a % 60) });
};
const dataAt = (s?: string | null) => (s ? `${clock(s)} · ${age(s)}` : i18n.t('ak_sld_no_data'));
/** Режим УЗЕ/PCS словами (ТЗ EMS box § 3.1: + розряд, − заряд). */
const modeOf = (p: number | null | undefined, status?: string | null) => {
  if (ok(p) && p > 0.5) return i18n.t('ak_sld_discharge');
  if (ok(p) && p < -0.5) return i18n.t('ak_sld_charge');
  if (/off|stop/i.test(status ?? '')) return i18n.t('ak_sld_stopped');
  return i18n.t('ak_sld_standby');
};
const posNum = (pos: string) => parseInt(pos, 10);
const qfNum = (b: string) => Number(b.split('QF')[1] ?? 0);
const short = (sn: string, n = 6) => (sn.length > n + 1 ? `…${sn.slice(-n)}` : sn);
const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
};
const freshestOf = (xs: (string | null | undefined)[]) => xs.filter(Boolean).sort().pop() ?? null;

// ─── причина стану ───────────────────────────────────────────────────────────
//
// Пояснення того, що ВЖЕ вирішили invState / pcsState: ті самі перевірки в тому
// самому порядку, лише замість стану — людський текст. null — стан нормальний.
// short — для плитки й переліків, full — для спливаючого вікна пристрою.

interface Reason { short: string; full: string }
interface Issue { who: string; cat: Cat; reason: Reason }

function invReason(live: InvLatest | undefined, night = false): Reason | null {
  if (!live) return { short: i18n.t('ak_sld_not_in_the_logger_poll'), full: i18n.t('ak_sld_the_inverter_is_not_in_the_last_logger') };
  const f = freshness(live.readAtLocal);
  if (f === 'nodata') return { short: i18n.t('ak_sld_no_link_2'), full: i18n.t('ak_sld_no_link_last_data_at', { c: clock(live.readAtLocal) }) };
  if (f === 'stale') return { short: i18n.t('ak_sld_data_is_stale'), full: i18n.t('ak_sld_data_is_stale_received', { a: age(live.readAtLocal) }) };
  const st = live.status ?? '';
  if (ALARM_RE.test(st)) return { short: i18n.t('ak_sld_alarm_2', { s: st }), full: i18n.t('ak_sld_the_device_reported_an_alarm', { s: st }) + (live.statusRaw != null ? i18n.t('ak_sld_code', { c: live.statusRaw }) : '') };
  if (nightIdle(live, night)) return null;
  if (live.quality === 'idle' && !night) return { short: i18n.t('ak_sld_data_quality_idle_in_daytime'), full: i18n.t('ak_sld_the_collector_flagged_data_quality_idl') };
  if (live.quality && live.quality !== 'ok') return { short: i18n.t('ak_sld_data_quality', { q: live.quality }), full: i18n.t('ak_sld_the_collector_flagged_data_quality_ins', { q: live.quality }) };
  if ((live.pacKw ?? 0) > 0.5) return null;
  if (st === 'ok' || IDLE_RE.test(st)) return null;
  return { short: i18n.t('ak_sld_unknown_status', { s: st ? st : '—' }), full: i18n.t('ak_sld_the_device_reported_an_unknown_status', { s: st ? st : '—' }) };
}

function pcsReason(u: PcsLive | undefined): Reason | null {
  if (!u) return { short: i18n.t('ak_sld_not_in_the_poll'), full: i18n.t('ak_sld_the_pcs_is_not_in_the_last_bess_poll') };
  const f = freshness(u.readAtLocal);
  if (f === 'nodata') return { short: i18n.t('ak_sld_no_link_2'), full: i18n.t('ak_sld_no_link_last_data_at', { c: clock(u.readAtLocal) }) };
  if (f === 'stale') return { short: i18n.t('ak_sld_data_is_stale'), full: i18n.t('ak_sld_data_is_stale_received', { a: age(u.readAtLocal) }) };
  if (u.alarmActive || ALARM_RE.test(u.status ?? '')) {
    const st = u.status ? ` «${u.status}»` : '';
    return { short: i18n.t('ak_sld_alarm_3', { s: st }), full: `${u.alarmActive ? i18n.t('ak_sld_the_pcs_reported_an_active_alarm') : i18n.t('ak_sld_the_device_reported_an_alarm_2', { s: st })}${u.alarmActive && st ? i18n.t('ak_sld_status', { s: st }) : ''}${u.statusRaw != null ? i18n.t('ak_sld_code', { c: u.statusRaw }) : ''}` };
  }
  if (u.quality && u.quality !== 'ok') return { short: i18n.t('ak_sld_data_quality', { q: u.quality }), full: i18n.t('ak_sld_the_collector_flagged_data_quality_ins', { q: u.quality }) };
  return null;
}

const CAT_RANK: Record<Cat, number> = { alarm: 0, warn: 1, nodata: 2, ok: 3 };
const byRank = (a: Issue, b: Issue) => CAT_RANK[a.cat] - CAT_RANK[b.cat];

/** Один рядок для плитки: що саме зіпсувало стан блока. */
function summarize(issues: Issue[], noun: string): { text: string; cat: Cat } | null {
  if (!issues.length) return null;
  const s = [...issues].sort(byRank);
  const top = s[0];
  if (s.length > 1 && s.every((x) => x.reason.short === top.reason.short)) {
    return { text: `${s.length} ${noun} — ${top.reason.short}`, cat: top.cat };
  }
  return { text: `${top.who} — ${top.reason.short}${s.length > 1 ? i18n.t('ak_sld_more', { n: s.length - 1 }) : ''}`, cat: top.cat };
}

/** Стани рядка причини, коли причин ще немає звідки взяти. */
const LOADING = { get text() { return i18n.t('ak_sld_loading_data'); }, cat: 'nodata' as Cat };
const NO_FEED = { get text() { return i18n.t('ak_sld_no_data_from_the_telemetry_server'); }, cat: 'nodata' as Cat };

/** Рядки «хто — чому» для спливаючого вікна блока (не більше max, решта — «ще N»). */
function issueRows(issues: Issue[], max = 6): { label: string; value: React.ReactNode }[] {
  const s = [...issues].sort(byRank);
  const rows = s.slice(0, max).map((x) => ({ label: x.who, value: <span style={{ color: CAT_META[x.cat].color }}>{x.reason.short}</span> }));
  if (s.length > max) rows.push({ label: i18n.t('ak_sld_more_2', { n: s.length - max }), value: <span style={{ color: scada.textMuted }}>{i18n.t('ak_sld_hover_over_a_device')}</span> });
  return rows;
}

// ─── вигляд: плитки й лінії ──────────────────────────────────────────────────

const tile: React.CSSProperties = {
  background: scada.panel, border: `1px solid ${scada.border}`, borderRadius: 12, boxShadow: 'var(--shadow)',
};
const LINE_OK = scada.state.ok;
const LINE_IDLE = 'var(--line-strong)';

function Dot({ c, size = 8, hollow }: { c: string; size?: number; hollow?: boolean }) {
  return <span aria-hidden="true" style={{ width: size, height: size, borderRadius: 999, flexShrink: 0, display: 'inline-block', background: hollow ? 'transparent' : c, border: `1.5px solid ${c}` }} />;
}

function StatePill({ cat }: { cat: Cat }) {
  const m = CAT_META[cat];
  return (
    <span className="inline-flex items-center" style={{ gap: 5, fontSize: 10.5, fontWeight: 700, color: m.color, whiteSpace: 'nowrap' }}>
      <Dot c={m.color} size={7} />{m.label}
    </span>
  );
}

/**
 * Рядок під заголовком плитки: що саме зіпсувало стан блока. Рядок є завжди
 * («Без відхилень», коли все гаразд) — так плитки в одному ряду мають однакову
 * висоту, і дерева під ними не роз'їжджаються.
 */
function ReasonLine({ s, hideWhenOk }: { s: { text: string; cat: Cat } | null; hideWhenOk?: boolean }) {
  if (!s) return hideWhenOk ? null : <span style={{ fontSize: 10.5, color: scada.textMuted, lineHeight: 1.3 }}>{i18n.t('ak_sld_no_deviations')}</span>;
  return (
    <span title={s.text} style={{ fontSize: 10.5, fontWeight: 600, lineHeight: 1.3, color: CAT_META[s.cat].color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {s.text}
    </span>
  );
}

/** Підпис «назва: значення» у статиці плитки. */
function Meta({ children, title, wrap }: { children: React.ReactNode; title?: string; wrap?: boolean }) {
  return <div title={title} style={{ fontSize: 10.5, lineHeight: 1.45, color: scada.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: wrap ? 'normal' : 'nowrap' }}>{children}</div>;
}
const Mono = ({ children }: { children: React.ReactNode }) => <span className="tabular-nums" style={{ color: scada.textDim }}>{children}</span>;

/** Вертикальний відвід усередині блока (від шини до плитки). */
function VLine({ left, h = 12, color, top }: { left: string; h?: number; color: string; top?: number }) {
  return <span aria-hidden="true" style={{ position: 'absolute', left, top: top ?? 0, width: 2, height: h, marginLeft: -1, background: color }} />;
}

// ─── вибір і спливаюче вікно ─────────────────────────────────────────────────

type Hover = { key: string; el: HTMLElement };

interface PopRow { label: string; value: React.ReactNode }
interface PopData { title: string; cat: Cat; stateText: string; rows: PopRow[]; note?: string }

/**
 * Спливаюче вікно — «матове скло»: щільний фон із розмиттям, тож дані під ним
 * не просвічують. Мишу не перехоплює (pointer-events: none) — наведення одразу
 * переходить на сусідню плитку. Позиція рахується один раз при появі (і при
 * прокрутці/зміні розміру вікна), не на кожен кадр. Стоїть під вузлом; біля
 * нижнього краю — над ним, біля правого — вирівнюється по правому краю вузла.
 */
function Popup({ anchor, data, popKey }: { anchor: HTMLElement; data: PopData; popKey: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; from: number } | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const on = () => setTick((n) => n + 1);
    window.addEventListener('scroll', on, true);
    window.addEventListener('resize', on);
    return () => { window.removeEventListener('scroll', on, true); window.removeEventListener('resize', on); };
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !anchor.isConnected) return;
    const r = anchor.getBoundingClientRect();
    const w = el.offsetWidth; const h = el.offsetHeight;
    const vw = window.innerWidth; const vh = window.innerHeight; const G = 8;
    let left = r.left;
    if (left + w > vw - 8) left = r.right - w;
    left = Math.max(8, Math.min(left, vw - w - 8));
    let top = r.bottom + G; let from = -4;
    if (top + h > vh - 8 && r.top - G - h >= 8) { top = r.top - G - h; from = 4; }
    top = Math.max(8, Math.min(top, vh - h - 8));
    setPos((p) => (p && p.left === left && p.top === top ? p : { left, top, from }));
  }, [anchor, tick, data.rows.length]);

  return createPortal(
    <div ref={ref} role="tooltip" key={popKey}
      style={{
        position: 'fixed', left: pos?.left ?? -9999, top: pos?.top ?? 0, zIndex: 80, width: 272,
        pointerEvents: 'none', visibility: pos ? 'visible' : 'hidden',
        background: `color-mix(in srgb, ${scada.panel} 86%, transparent)`,
        WebkitBackdropFilter: 'blur(14px) saturate(1.2)', backdropFilter: 'blur(14px) saturate(1.2)',
        border: `1px solid ${scada.borderStrong}`, borderRadius: 12, boxShadow: 'var(--shadow)',
        padding: '10px 12px', display: 'grid', gap: 8,
        animation: pos ? 'sldPopIn 170ms ease-out both' : 'none',
        ['--sld-from' as string]: `${pos?.from ?? -4}px`,
      } as React.CSSProperties}>
      <div className="flex items-center justify-between" style={{ gap: 8 }}>
        <span className="font-display" style={{ fontSize: 13.5, fontWeight: 700, color: scada.text }}>{data.title}</span>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: CAT_META[data.cat].color, whiteSpace: 'nowrap' }}>{data.stateText}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 12 }}>
        {data.rows.map((r) => (
          <Fragment key={r.label}>
            <div style={{ fontSize: 11, color: scada.textMuted, padding: '4px 0', borderTop: `1px solid ${scada.border}`, whiteSpace: 'nowrap' }}>{r.label}</div>
            <div className="tabular-nums" style={{ fontSize: 12, color: scada.text, padding: '4px 0', borderTop: `1px solid ${scada.border}`, textAlign: 'right' }}>{r.value}</div>
          </Fragment>
        ))}
      </div>
      {data.note && <div style={{ fontSize: 10.5, color: scada.textMuted }}>{data.note}</div>}
    </div>,
    document.body,
  );
}

const POP_CSS = `
@keyframes sldPopIn { from { opacity: 0; transform: translateY(var(--sld-from, -4px)) scale(.985); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { [role="tooltip"] { animation: none !important; } }
`;

// ─── компонент ───────────────────────────────────────────────────────────────

export default function StationSld({ assetId, topology, drawing, limitKw }: {
  assetId: string;
  topology: StationTopology;
  drawing: StationDrawing;
  /** Діюче обмеження потужності станції, кВт (з телеметрії станції), якщо є. */
  limitKw?: number | null;
}) {
  useTranslation();
  const { data: invData, err: invErr } = useInverterLatest(assetId);
  const { data: bessData, err: bessErr } = useBessLatest(assetId);
  const [hover, setHover] = useState<Hover | null>(null);
  const [pinned, setPinned] = useState<Hover | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const [width, setWidth] = useState(1400);
  const rootRef = useRef<HTMLDivElement>(null);

  const liveByUnit = useMemo(() => new Map((invData ?? []).map((x) => [x.unitId, x] as const)), [invData]);
  const pcsByName = useMemo(() => new Map((bessData?.units ?? []).map((u) => [u.pcsName, u as PcsLive] as const)), [bessData]);
  const batById = useMemo(() => new Map((bessData?.batteries ?? []).map((b) => [b.batteryId, b] as const)), [bessData]);
  const NOM = drawing.ratings.inverterKw;

  const night = useMemo(() => isNight(invData), [invData]);

  // ── моделі вузлів із конфігу + телеметрії ──
  const bess = useMemo(() => topology.bess.map((b) => {
    const banks = b.banks.map((bank) => {
      const pcs = bank.pcs.map((p) => { const live = pcsByName.get(p.pos); return { cfg: p, live, state: pcsState(live), reason: pcsReason(live) }; });
      const bat = batById.get(posNum(bank.pos));
      const power = pcs.reduce((s, p) => s + (p.live?.powerKw ?? 0), 0);
      return { cfg: bank, pcs, bat, power, state: worst(pcs.map((p) => p.state)), at: freshestOf(pcs.map((p) => p.live?.readAtLocal)) };
    });
    const pcsAll = banks.flatMap((x) => x.pcs);
    const socs = banks.map((x) => x.bat?.socPct).filter(ok);
    const noFeed = !!bessErr && !bessData;
    const loading = !bessData && !bessErr;
    const issues: Issue[] = noFeed || loading ? [] : pcsAll.filter((p) => p.reason).map((p) => ({ who: `PCS ${p.cfg.pos}`, cat: CAT_OF[p.state], reason: p.reason! }));
    return {
      cfg: b, banks, pcsAll, issues,
      summary: loading ? LOADING : noFeed ? NO_FEED : summarize(issues, 'PCS'),
      // Позначення блока на кресленні (T1…T4) — префікс назв його PCS (T1.1…T1.4).
      feeder: b.banks[0]?.pcs[0]?.pos.split('.')[0] ?? '',
      power: pcsAll.reduce((s, p) => s + (p.live?.powerKw ?? 0), 0),
      soc: socs.length ? socs.reduce((a, c) => a + c, 0) / socs.length : null,
      pcsOnline: pcsAll.filter((p) => p.state !== 'nodata' && p.state !== 'stale').length,
      banksOnline: banks.filter((x) => ok(x.bat?.socPct)).length,
      state: bessErr && !bessData ? 'nodata' as NodeState : worst(pcsAll.map((p) => p.state)),
      at: freshestOf(pcsAll.map((p) => p.live?.readAtLocal)),
    };
  }), [topology, pcsByName, batById, bessErr, bessData]);

  const ktps = useMemo(() => topology.pv.map((k) => {
    const groups = k.groups.map((g) => {
      const inv = [...g.inverters].sort((a, b) => posNum(a.pos) - posNum(b.pos))
        .map((i) => { const live = liveByUnit.get(i.unit_id); return { cfg: i, live, state: invState(live, night), reason: invReason(live, night) }; });
      const fresh = inv.filter((x) => x.state !== 'nodata' && x.state !== 'stale');
      const qf = inv.map((x) => x.cfg.breaker).sort((a, b) => qfNum(a) - qfNum(b));
      return {
        cfg: g, inv,
        range: inv.length ? `${inv[0].cfg.pos}–${inv[inv.length - 1].cfg.pos}` : '—',
        breakers: qf.length ? `${qf[0]}–${qf[qf.length - 1]}` : '—',
        pac: fresh.reduce((s, x) => s + (x.live?.pacKw ?? 0), 0),
        online: fresh.length,
        state: invErr && !invData ? 'nodata' as NodeState : worst(inv.map((x) => x.state)),
      };
    });
    const all = groups.flatMap((g) => g.inv);
    const noFeed = !!invErr && !invData;
    const loading = !invData && !invErr;
    const issues: Issue[] = noFeed || loading ? [] : all.filter((x) => x.reason).map((x) => ({ who: x.cfg.pos, cat: CAT_OF[x.state], reason: x.reason! }));
    return {
      cfg: k, groups, count: all.length, issues,
      summary: loading ? LOADING : noFeed ? NO_FEED : summarize(issues, i18n.t('ak_sld_inv')),
      pac: groups.reduce((s, g) => s + g.pac, 0),
      online: groups.reduce((s, g) => s + g.online, 0),
      state: invErr && !invData ? 'nodata' as NodeState : worst(all.map((x) => x.state)),
      at: freshestOf(all.map((x) => x.live?.readAtLocal)),
      hz: median(all.filter((x) => x.state !== 'nodata' && x.state !== 'stale').map((x) => x.live?.facHz).filter(ok)),
    };
  }), [topology, liveByUnit, invErr, invData, night]);

  const pvKw = ktps.reduce((s, k) => s + k.pac, 0);
  const bessKw = bess.reduce((s, b) => s + b.power, 0);
  const pccKw = pvKw + bessKw;
  const hz = median(ktps.map((k) => k.hz).filter(ok));
  const stationState = worst([...bess.map((b) => b.state), ...ktps.map((k) => k.state)]);
  // Причина стану станції — те саме по всіх блоках, з назвою блока перед пристроєм.
  const stationIssues: Issue[] = [
    ...bess.flatMap((u) => u.issues.map((x) => ({ ...x, who: `${cx(u.cfg.pos)} ${x.who}` }))),
    ...ktps.flatMap((k) => k.issues.map((x) => ({ ...x, who: `${cx(k.cfg.pos)} ${x.who}` }))),
  ];
  const blocks = [...bess, ...ktps];
  const stationSummary = blocks.some((x) => x.summary === LOADING) ? LOADING
    : blocks.some((x) => x.summary === NO_FEED) ? NO_FEED
    : summarize(stationIssues, i18n.t('ak_sld_dev'));
  const invTotal = ktps.reduce((s, k) => s + k.count, 0);
  const banksTotal = bess.reduce((s, b) => s + b.banks.length, 0);
  const racksTotal = bess.reduce((s, b) => s + b.banks.reduce((n, x) => n + x.cfg.racks, 0), 0);

  // ── розкладка під ширину (ряди переносяться, нічого не налазить) ──
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const uzeCols = width >= 1180 ? 4 : width >= 620 ? 2 : 1;
  const ktpCols = width >= 1180 ? 3 : width >= 820 ? 2 : 1;

  // ── наведення / фокус / дотик ──
  const shown = hover ?? pinned;
  const bind = (key: string, extra?: { onClick?: () => void }) => ({
    'data-sld-node': key,
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => setHover({ key, el: e.currentTarget }),
    onMouseLeave: () => setHover(null),
    onFocus: (e: React.FocusEvent<HTMLElement>) => setHover({ key, el: e.currentTarget }),
    onBlur: () => setHover(null),
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      const el = e.currentTarget;
      setPinned((cur) => (cur?.key === key ? null : { key, el }));
      extra?.onClick?.();
    },
  });
  useEffect(() => {
    if (!pinned && !opened) return;
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') { setPinned(null); setHover(null); setOpened(null); } };
    const onDown = (ev: PointerEvent) => {
      const t = ev.target as HTMLElement | null;
      if (t?.closest('[data-sld-node]')) return;
      setPinned(null);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown); };
  }, [pinned, opened]);

  // Підсвічений блок верхнього рівня (для ліній): наведений або «відкритий».
  const blockOf = (key: string | undefined) => {
    if (!key) return null;
    const [kind, a] = key.split(':');
    if (kind === 'uze' || kind === 'ktp') return `${kind}:${a}`;
    if (kind === 'pcs' || kind === 'bank' || kind === 'rg') {
      const b = bess.find((x) => x.banks.some((bk) => bk.cfg.pos === a || bk.pcs.some((p) => p.cfg.pos === a)));
      return b ? `uze:${b.cfg.pos}` : null;
    }
    if (kind === 'grp' || kind === 'inv') {
      const k = ktps.find((x) => (kind === 'grp' ? x.cfg.pos === a : x.groups.some((g) => g.inv.some((i) => String(i.cfg.unit_id) === a))));
      return k ? `ktp:${k.cfg.pos}` : null;
    }
    return null;
  };
  const activeBlock = blockOf(shown?.key) ?? (opened ? opened : null);

  // ── вміст спливаючого вікна: лише динаміка ──
  const popData = (key: string): PopData | null => {
    const [kind, a, b] = key.split(':');
    if (kind === 'pcc') {
      return {
        title: i18n.t('ak_sld_point_of_connection'), cat: CAT_OF[stationState], stateText: i18n.t('ak_sld_estimate'),
        rows: [
          ...(stationSummary ? [{ label: i18n.t('ak_sld_reason'), value: <span style={{ color: CAT_META[stationSummary.cat].color }}>{stationSummary.text}</span> }] : []),
          { label: i18n.t('ak_sld_pv_plant_sum_of_inverters'), value: `${mwOrKw(pvKw)}` },
          { label: i18n.t('ak_sld_bess', { m: modeOf(bessKw).toLowerCase() }), value: mwOrKw(Math.abs(bessKw)) },
          { label: i18n.t('ak_sld_inverter_data'), value: dataAt(freshestOf(ktps.map((k) => k.at))) },
          { label: i18n.t('ak_sld_bess_data'), value: dataAt(freshestOf(bess.map((x) => x.at))) },
        ],
        note: i18n.t('ak_sld_the_power_at_the_point_of_connection_s'),
      };
    }
    if (kind === 'uze') {
      const u = bess.find((x) => x.cfg.pos === a); if (!u) return null;
      const dis = u.pcsAll.reduce((s, p) => s + (p.live?.dischargeAvailableKwh ?? 0), 0);
      const chg = u.pcsAll.reduce((s, p) => s + (p.live?.chargeAvailableKwh ?? 0), 0);
      const tMax = Math.max(...u.banks.map((x) => x.bat?.tempMaxC ?? -Infinity));
      const hours = u.power > 0.5 ? dis / u.power : u.power < -0.5 ? chg / -u.power : null;
      return {
        title: cx(u.cfg.pos), cat: CAT_OF[u.state], stateText: modeOf(u.power),
        rows: [
          { label: i18n.t('ak_sld_can_discharge'), value: u.pcsAll.some((p) => p.live) ? `${nf1.format(dis / 1000)} ${i18n.t('ak_sld_mwh')}` : '—' },
          { label: i18n.t('ak_sld_can_charge'), value: u.pcsAll.some((p) => p.live) ? `${nf1.format(chg / 1000)} ${i18n.t('ak_sld_mwh')}` : '—' },
          ...(hours != null && isFinite(hours) ? [{ label: u.power > 0 ? i18n.t('ak_sld_to_full_discharge') : i18n.t('ak_sld_to_full_charge'), value: `≈ ${nf1.format(hours)} ${i18n.t('ak_sld_h')}` }] : []),
          { label: i18n.t('ak_sld_cell_temperature_max'), value: isFinite(tMax) ? `${nf1.format(tMax)} °C` : '—' },
          { label: i18n.t('ak_sld_data'), value: dataAt(u.at) },
          ...issueRows(u.issues),
        ],
      };
    }
    if (kind === 'pcs') {
      for (const u of bess) for (const bk of u.banks) for (const p of bk.pcs) {
        if (p.cfg.pos !== a) continue;
        const l = p.live;
        return {
          title: `PCS ${p.cfg.pos}`, cat: CAT_OF[p.state], stateText: PCS_LABEL[p.state],
          rows: l ? [
            ...(p.reason ? [{ label: i18n.t('ak_sld_reason'), value: <span style={{ color: CAT_META[CAT_OF[p.state]].color }}>{p.reason.full}</span> }] : []),
            { label: i18n.t('ak_sld_mode'), value: modeOf(l.powerKw, l.status) },
            { label: i18n.t('ak_sld_power'), value: `${kw(Math.abs(l.powerKw ?? 0))} ${i18n.t('ak_sld_kw')}` },
            { label: i18n.t('ak_sld_load'), value: ok(l.loadRatioPct) ? `${nf0.format(l.loadRatioPct)} %` : '—' },
            { label: i18n.t('ak_sld_temperature'), value: ok(l.innerTempC) ? `${nf1.format(l.innerTempC)} °C` : ok(l.radiatorTempC) ? `${nf1.format(l.radiatorTempC)} °C` : '—' },
            { label: i18n.t('ak_sld_allowed_charge_discharge'), value: `${kw(l.maxChargeKw)} / ${kw(l.maxDischargeKw)} ${i18n.t('ak_sld_kw')}` },
            { label: i18n.t('ak_sld_device_status'), value: l.status || '—' },
            { label: i18n.t('ak_sld_data'), value: dataAt(l.readAtLocal) },
          ] : [{ label: i18n.t('ak_sld_data'), value: i18n.t('ak_sld_not_in_the_poll') }],
        };
      }
      return null;
    }
    if (kind === 'bank') {
      for (const u of bess) for (const bk of u.banks) {
        if (bk.cfg.pos !== a) continue;
        const [pa, pb] = bk.pcs;
        const sa = pa?.live?.socPct; const sb = pb?.live?.socPct;
        return {
          title: bk.cfg.pos, cat: CAT_OF[bk.state], stateText: modeOf(bk.power),
          rows: [
            { label: 'SoH', value: ok(bk.bat?.sohPct) ? `${nf0.format(bk.bat.sohPct)} %` : '—' },
            { label: `SoC Group ${pa?.cfg.rack_group ?? 'A'} / ${pb?.cfg.rack_group ?? 'B'}`, value: `${ok(sa) ? nf0.format(sa) : '—'} / ${ok(sb) ? nf0.format(sb) : '—'} %` },
            { label: i18n.t('ak_sld_soc_between_groups'), value: ok(sa) && ok(sb) ? `${nf0.format(Math.abs(sa - sb))} %` : '—' },
            { label: i18n.t('ak_sld_cell_temperature'), value: ok(bk.bat?.tempMinC) && ok(bk.bat?.tempMaxC) ? `${nf1.format(bk.bat.tempMinC)}–${nf1.format(bk.bat.tempMaxC)} °C` : '—' },
            { label: i18n.t('ak_sld_cell_voltage'), value: ok(bk.bat?.cellVminMv) && ok(bk.bat?.cellVmaxMv) ? `${nf0.format(bk.bat.cellVminMv)}–${nf0.format(bk.bat.cellVmaxMv)} ${i18n.t('ak_sld_mv')}` : '—' },
            { label: i18n.t('ak_sld_power'), value: `${kw(Math.abs(bk.power))} ${i18n.t('ak_sld_kw')}` },
            { label: i18n.t('ak_sld_data'), value: dataAt(bk.at) },
            ...issueRows(bk.pcs.filter((x) => x.reason).map((x) => ({ who: `PCS ${x.cfg.pos}`, cat: CAT_OF[x.state], reason: x.reason! }))),
          ],
        };
      }
      return null;
    }
    if (kind === 'rg') {
      for (const u of bess) for (const bk of u.banks) {
        if (bk.cfg.pos !== a) continue;
        const p = bk.pcs.find((x) => x.cfg.rack_group === b); if (!p) return null;
        const other = bk.pcs.find((x) => x !== p);
        const l = p.live; const so = other?.live?.socPct;
        return {
          title: i18n.t('ak_sld_racks_group', { r: p.cfg.racks, g: p.cfg.rack_group }), cat: CAT_OF[p.state], stateText: modeOf(l?.powerKw, l?.status),
          rows: l ? [
            ...(p.reason ? [{ label: i18n.t('ak_sld_reason'), value: <span style={{ color: CAT_META[CAT_OF[p.state]].color }}>{p.reason.full}</span> }] : []),
            { label: 'SoC', value: ok(l.socPct) ? `${nf0.format(l.socPct)} %` : '—' },
            { label: i18n.t('ak_sld_soc_vs_group', { g: other?.cfg.rack_group ?? '' }), value: ok(l.socPct) && ok(so) ? `${nf0.format(Math.abs(l.socPct - so))} %` : '—' },
            { label: i18n.t('ak_sld_voltage_current'), value: `${ok(l.batteryV) ? nf0.format(l.batteryV) : '—'} ${i18n.t('ak_sld_v')} / ${ok(l.batteryA) ? nf0.format(l.batteryA) : '—'} ${i18n.t('ak_sld_a')}` },
            { label: i18n.t('ak_sld_allowed_charge_discharge'), value: `${kw(l.maxChargeKw)} / ${kw(l.maxDischargeKw)} ${i18n.t('ak_sld_kw')}` },
            { label: i18n.t('ak_sld_data'), value: dataAt(l.readAtLocal) },
          ] : [{ label: i18n.t('ak_sld_data'), value: i18n.t('ak_sld_not_in_the_poll') }],
        };
      }
      return null;
    }
    if (kind === 'ktp') {
      const k = ktps.find((x) => x.cfg.pos === a); if (!k) return null;
      return {
        title: cx(k.cfg.pos), cat: CAT_OF[k.state], stateText: k.state === 'nodata' ? i18n.t('ak_sld_no_data') : `${nf0.format((k.pac / (k.count * NOM)) * 100)} % ${i18n.t('ak_sld_of_nominal')}`,
        rows: [
          ...k.groups.map((g) => ({ label: i18n.t('ak_sld_group', { g: g.cfg.group }), value: `${kw(g.pac)} ${i18n.t('ak_sld_kw')} · ${g.online}/${g.inv.length}` })),
          { label: i18n.t('ak_sld_frequency'), value: ok(k.hz) ? `${nf2.format(k.hz)} ${i18n.t('ak_sld_hz')}` : '—' },
          { label: i18n.t('ak_sld_logger_data'), value: dataAt(k.at) },
          ...issueRows(k.issues),
        ],
      };
    }
    if (kind === 'grp') {
      const k = ktps.find((x) => x.cfg.pos === a); const g = k?.groups.find((x) => x.cfg.group === b); if (!k || !g) return null;
      const bad: Issue[] = g.inv.filter((x) => x.reason).map((x) => ({ who: x.cfg.pos, cat: CAT_OF[x.state], reason: x.reason! }));
      return {
        title: i18n.t('ak_sld_group_2', { p: cx(k.cfg.pos), g: g.cfg.group }), cat: CAT_OF[g.state], stateText: `${g.online}/${g.inv.length} ${i18n.t('ak_sld_online_2')}`,
        rows: [
          { label: i18n.t('ak_sld_of_nominal_2'), value: g.inv.length ? `${nf0.format((g.pac / (g.inv.length * NOM)) * 100)} %` : '—' },
          { label: i18n.t('ak_sld_data'), value: dataAt(freshestOf(g.inv.map((x) => x.live?.readAtLocal))) },
          ...(bad.length ? issueRows(bad, 8) : [{ label: i18n.t('ak_sld_deviations'), value: i18n.t('ak_sld_none') }]),
        ],
      };
    }
    if (kind === 'inv') {
      for (const k of ktps) for (const g of k.groups) for (const x of g.inv) {
        if (String(x.cfg.unit_id) !== a) continue;
        const l = x.live; const fresh = x.state !== 'nodata';
        return {
          title: `${x.cfg.pos} · ${x.cfg.breaker}`, cat: CAT_OF[x.state], stateText: INV_LABEL[x.state],
          rows: l ? [
            ...(x.reason ? [{ label: i18n.t('ak_sld_reason'), value: <span style={{ color: CAT_META[CAT_OF[x.state]].color }}>{x.reason.full}</span> }] : []),
            { label: i18n.t('ak_sld_ac_power'), value: fresh ? `${kw(l.pacKw)} ${i18n.t('ak_sld_kw')}` : '—' },
            { label: i18n.t('ak_sld_of_nominal_2'), value: fresh && ok(l.pacKw) ? `${nf0.format((l.pacKw / NOM) * 100)} %` : '—' },
            { label: i18n.t('ak_sld_dc_power'), value: fresh ? `${kw(l.ppvKw)} ${i18n.t('ak_sld_kw')}` : '—' },
            { label: i18n.t('ak_sld_frequency'), value: fresh && ok(l.facHz) ? `${nf2.format(l.facHz)} ${i18n.t('ak_sld_hz')}` : '—' },
            { label: i18n.t('ak_sld_device_status'), value: l.status || '—' },
            { label: i18n.t('ak_sld_data'), value: dataAt(l.readAtLocal) },
          ] : [{ label: i18n.t('ak_sld_data'), value: i18n.t('ak_sld_not_in_the_logger_poll') }],
        };
      }
      return null;
    }
    return null;
  };

  const pop = shown && shown.el.isConnected ? popData(shown.key) : null;

  // ── лінії від PCC до блоків (каскад) ──
  const [geom, setGeom] = useState<{ w: number; h: number; segs: { d: string; key: string; color: string }[] } | null>(null);
  const blockColor = (key: string) => {
    const [kind, a] = key.split(':');
    const st = kind === 'uze' ? bess.find((x) => x.cfg.pos === a)?.state : ktps.find((x) => x.cfg.pos === a)?.state;
    if (!st) return LINE_IDLE;
    const c = CAT_OF[st];
    if (c === 'ok') return st === 'gen' || (kind === 'uze' && Math.abs(bess.find((x) => x.cfg.pos === a)?.power ?? 0) > 0.5) || (kind === 'ktp' && (ktps.find((x) => x.cfg.pos === a)?.pac ?? 0) > 0.5) ? LINE_OK : LINE_IDLE;
    return CAT_META[c].color;
  };
  const colorSig = [...bess.map((b) => blockColor(`uze:${b.cfg.pos}`)), ...ktps.map((k) => blockColor(`ktp:${k.cfg.pos}`))].join('|');

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const measure = () => {
      const rr = root.getBoundingClientRect();
      const box = (el: Element) => { const r = el.getBoundingClientRect(); return { x: r.left - rr.left + r.width / 2, top: r.top - rr.top, bottom: r.bottom - rr.top }; };
      const pccEl = root.querySelector('[data-sld-anchor="pcc"]');
      if (!pccEl) return;
      const pcc = box(pccEl);
      const GUT = 9;
      const tiers = ['uze', 'ktp'].map((t) => Array.from(root.querySelectorAll(`[data-sld-anchor^="${t}:"]`)).map((el) => ({ key: el.getAttribute('data-sld-anchor')!, ...box(el) })));
      // Підряди (після перенесення) — групи плиток з однаковим верхом.
      const rows: { busY: number; items: { key: string; x: number; top: number }[] }[] = [];
      tiers.forEach((items) => {
        const byTop = new Map<number, typeof items>();
        items.forEach((it) => { const k = Math.round(it.top); byTop.set(k, [...(byTop.get(k) ?? []), it]); });
        [...byTop.entries()].sort((a, b) => a[0] - b[0]).forEach(([top, its]) => rows.push({ busY: top - 18, items: its }));
      });
      if (!rows.length) return;
      const trunk = blockColor('trunk');
      const segs: { d: string; key: string; color: string }[] = [];
      const y0 = rows[0].busY;
      segs.push({ d: `M ${pcc.x} ${pcc.bottom} V ${y0}`, key: 'trunk', color: trunk });
      const yLast = rows[rows.length - 1].busY;
      if (yLast > y0) segs.push({ d: `M ${GUT} ${y0} V ${yLast}`, key: 'trunk', color: trunk });
      rows.forEach((r, i) => {
        const xs = r.items.map((it) => it.x);
        const right = Math.max(...xs, i === 0 ? pcc.x : -Infinity);
        segs.push({ d: `M ${GUT} ${r.busY} H ${right}`, key: 'trunk', color: trunk });
        r.items.forEach((it) => segs.push({ d: `M ${it.x} ${r.busY} V ${it.top}`, key: it.key, color: blockColor(it.key) }));
      });
      setGeom({ w: rr.width, h: rr.height, segs });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [uzeCols, ktpCols, colorSig, invData == null, bessData == null]);

  // Колір магістралі: зелена, якщо станція на звʼязку, інакше сіра.
  function trunkColor() { return CAT_OF[stationState] === 'nodata' ? CAT_META.nodata.color : LINE_OK; }
  const segColor = (s: { key: string; color: string }) => (s.key === 'trunk' ? trunkColor() : s.color);

  const sectionTitle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: scada.text, letterSpacing: '.02em' };

  return (
    <div ref={rootRef} style={{ position: 'relative', padding: '4px 0 8px 22px', minWidth: 0 }}>
      <style>{POP_CSS}</style>
      {geom && (
        <svg width={geom.w} height={geom.h} aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', overflow: 'visible' }}>
          {geom.segs.map((s, i) => {
            const hot = activeBlock != null && s.key === activeBlock;
            const dim = activeBlock != null && s.key !== 'trunk' && !hot;
            return <path key={i} d={s.d} fill="none" stroke={segColor(s)} strokeWidth={hot ? 3 : 1.8} strokeLinecap="round"
              opacity={dim ? 0.35 : 1} style={{ transition: 'opacity .15s, stroke-width .15s' }} />;
          })}
        </svg>
      )}

      {/* ── PCC ── */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button type="button" data-sld-anchor="pcc" {...bind('pcc')}
          style={{ ...tile, position: 'relative', zIndex: 1, padding: '12px 16px', textAlign: 'left', cursor: 'default', maxWidth: 640, justifySelf: 'center', display: 'grid', gap: 8 }}>
          <span className="flex items-center justify-between" style={{ gap: 12 }}>
            <span className="font-display" style={{ fontSize: 15, fontWeight: 700, color: scada.text }}>{i18n.t('ak_sld_point_of_connection_pcc')}</span>
            <StatePill cat={CAT_OF[stationState]} />
          </span>
          <ReasonLine s={stationSummary} hideWhenOk />
          <span style={{ display: 'grid' }}>
            <Meta>{cx(drawing.grid.substation)} · {cx(drawing.grid.transformer)} · {cx(drawing.grid.transformerRating)} · {cx(drawing.grid.switchgear)}</Meta>
            <Meta>{i18n.t('ak_sld_measurement')} {topology.pcc.device} · <Mono>{topology.pcc.ip}:{topology.pcc.port}</Mono> · Slave ID {topology.pcc.slave_id} · EMS box <Mono>{topology.ems_box.ip}</Mono></Meta>
          </span>
          <span style={{ display: 'grid', gridTemplateColumns: 'repeat(3, auto)', justifyContent: 'start', columnGap: 22, borderTop: `1px solid ${scada.border}`, paddingTop: 8 }}>
            <Kpi label={i18n.t('ak_sld_p_estimate', { d: pccKw >= 0 ? i18n.t('ak_sld_export') : i18n.t('ak_sld_import') })} value={invData || bessData ? mwOrKw(Math.abs(pccKw)) : '—'} />
            <Kpi label={i18n.t('ak_sld_limit')} value={ok(limitKw) ? mwOrKw(limitKw) : i18n.t('ak_sld_none')} />
            <Kpi label={i18n.t('ak_sld_frequency_inverters')} value={ok(hz) ? `${nf2.format(hz)} ${i18n.t('ak_sld_hz')}` : '—'} />
          </span>
        </button>
      </div>

      {/* ── ряд 1: УЗЕ ── */}
      <div style={{ marginTop: 30 }}>
        <div style={{ ...sectionTitle, maxWidth: '44%' }}>
          {i18n.t('ak_sld_energy_storage_bess')}
          <span style={{ fontWeight: 400, color: scada.textMuted }}>{i18n.t('ak_sld_banks_racks', { n: bess.length, v: drawing.vendors.bess, s: topology.bess[0]?.system, b: banksTotal, bv: drawing.vendors.bank, r: racksTotal })}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${uzeCols}, minmax(0, 1fr))`, columnGap: 16, rowGap: 40, marginTop: 34 }}>
          {bess.map((u) => (
            <UzeBlock key={u.cfg.pos} u={u} drawing={drawing} bind={bind} active={activeBlock === `uze:${u.cfg.pos}`}
              onOpen={() => setOpened(`uze:${u.cfg.pos}`)} />
          ))}
        </div>
      </div>

      {/* ── ряд 2: КТП ── */}
      <div style={{ marginTop: 34 }}>
        <div style={{ ...sectionTitle, maxWidth: '60%' }}>
          {i18n.t('ak_sld_solar_generation')}
          <span style={{ fontWeight: 400, color: scada.textMuted }}>{i18n.t('ak_sld_ktp_inverters_panels', { n: ktps.length, i: invTotal, v: drawing.vendors.pv, m: topology.pv[0]?.groups[0]?.inverters[0]?.model, p: cx(drawing.panels) })}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${ktpCols}, minmax(0, 1fr))`, columnGap: 16, rowGap: 40, marginTop: 34 }}>
          {ktps.map((k) => (
            <KtpBlock key={k.cfg.pos} k={k} drawing={drawing} nominalKw={NOM} bind={bind} active={activeBlock === `ktp:${k.cfg.pos}`}
              onOpen={() => setOpened(`ktp:${k.cfg.pos}`)} />
          ))}
        </div>
      </div>

      {pop && shown && <Popup key={shown.key} anchor={shown.el} data={pop} popKey={shown.key} />}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: 'grid', minWidth: 0 }}>
      <span style={{ fontSize: 10, color: scada.textMuted, whiteSpace: 'nowrap' }}>{label}</span>
      <span className="font-display tabular-nums" style={{ fontSize: 18, fontWeight: 700, color: scada.text, lineHeight: 1.2 }}>{value}</span>
    </span>
  );
}

type Bind = (key: string, extra?: { onClick?: () => void }) => React.HTMLAttributes<HTMLElement> & { 'data-sld-node': string };

// ─── блок УЗЕ: УЗЕ → 4 PCS → 2 банки → групи стійок ─────────────────────────

interface PcsView { cfg: TopoPcs; live?: PcsLive; state: NodeState; reason: Reason | null }
interface BankView { cfg: TopoBank; pcs: PcsView[]; bat?: BessBattery; power: number; state: NodeState; at: string | null }
interface UzeView {
  cfg: TopoBess; banks: BankView[]; pcsAll: PcsView[]; feeder: string; power: number; soc: number | null;
  pcsOnline: number; banksOnline: number; state: NodeState; at: string | null;
  issues: Issue[]; summary: { text: string; cat: Cat } | null;
}

function UzeBlock({ u, drawing, bind, active, onOpen }: { u: UzeView; drawing: StationDrawing; bind: Bind; active: boolean; onOpen: () => void }) {
  const lineC = (s: NodeState) => (CAT_OF[s] === 'ok' ? (s === 'gen' ? LINE_OK : LINE_IDLE) : CAT_META[CAT_OF[s]].color);
  const nPcs = u.pcsAll.length;
  return (
    <div style={{ minWidth: 0, display: 'grid', alignContent: 'start', position: 'relative', zIndex: 1 }}>
      <button type="button" data-sld-anchor={`uze:${u.cfg.pos}`} {...bind(`uze:${u.cfg.pos}`, { onClick: onOpen })}
        style={{ ...tile, padding: '10px 12px', textAlign: 'left', cursor: 'pointer', display: 'grid', gap: 6,
                 borderColor: active ? scada.borderStrong : scada.border, boxShadow: active ? `0 0 0 1px ${scada.borderStrong}, var(--shadow)` : 'var(--shadow)' }}>
        <span className="flex items-center justify-between" style={{ gap: 8 }}>
          <span className="font-display" style={{ fontSize: 15, fontWeight: 700, color: scada.text }}>{cx(u.cfg.pos)} <span style={{ fontSize: 11, fontWeight: 600, color: scada.textMuted }}>· {u.feeder}</span></span>
          <StatePill cat={CAT_OF[u.state]} />
        </span>
        <ReasonLine s={u.summary} />
        <span style={{ display: 'grid' }}>
          <Meta>{drawing.vendors.bess} {u.cfg.system} · {u.cfg.bcs}</Meta>
          <Meta title={u.cfg.sn}>S/N <Mono>{u.cfg.sn}</Mono></Meta>
          <Meta>{cx(drawing.ratings.bessBlock)}</Meta>
        </span>
        <span style={{ display: 'grid', gap: 2, borderTop: `1px solid ${scada.border}`, paddingTop: 6 }}>
          <span className="font-display tabular-nums" style={{ fontSize: 18, fontWeight: 700, color: scada.text, whiteSpace: 'nowrap' }}>
            {u.pcsAll.some((p) => p.live) ? `${kw(Math.abs(u.power))}` : '—'}<span style={{ fontSize: 11, fontWeight: 500, color: scada.textMuted }}> {i18n.t('ak_sld_kw')} · {modeOf(u.power)}</span>
          </span>
          <span className="tabular-nums" style={{ fontSize: 11, color: scada.textDim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            SoC {ok(u.soc) ? `${nf0.format(u.soc)} %` : '—'} · PCS {u.pcsOnline}/{nPcs} · {i18n.t('ak_sld_banks')} {u.banksOnline}/{u.banks.length}
          </span>
        </span>
      </button>

      {/* Дерево: магістраль блока зліва, від неї — пари PCS, від кожного PCS — своя група стійок банку. */}
      <div style={{ position: 'relative', paddingLeft: 14 }}>
        {u.banks.map((bk, i) => {
          const last = i === u.banks.length - 1;
          return (
            <div key={bk.cfg.pos} style={{ position: 'relative', paddingTop: 16, paddingBottom: last ? 0 : 6 }}>
              {/* магістраль блока і відвід до пари PCS */}
              <span aria-hidden="true" style={{ position: 'absolute', left: -8, top: 0, width: 2, height: last ? 9 : '100%', background: lineC(u.state) }} />
              <span aria-hidden="true" style={{ position: 'absolute', left: -8, top: 8, width: 'calc(75% + 8px)', height: 2, background: lineC(bk.state) }} />
              <VLine left="25%" top={8} h={8} color={lineC(bk.pcs[0]?.state ?? 'nodata')} />
              <VLine left="75%" top={8} h={8} color={lineC(bk.pcs[1]?.state ?? 'nodata')} />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                {bk.pcs.map((p) => {
                  const c = CAT_META[CAT_OF[p.state]].color;
                  return (
                    <div key={p.cfg.pos} style={{ padding: '0 4px', minWidth: 0 }}>
                      <button type="button" {...bind(`pcs:${p.cfg.pos}`)}
                        style={{ ...tile, width: '100%', padding: '7px 8px', textAlign: 'left', cursor: 'pointer', display: 'grid', gap: 1,
                                 borderColor: CAT_OF[p.state] === 'ok' ? scada.border : `color-mix(in srgb, ${c} 60%, transparent)` }}>
                        <span className="flex items-center justify-between" style={{ gap: 6 }}>
                          <span className="font-display" style={{ fontSize: 12.5, fontWeight: 700, color: scada.text }}>PCS {p.cfg.pos}</span>
                          <Dot c={c} size={8} hollow={p.state === 'idle' || p.state === 'stale'} />
                        </span>
                        <Meta>{p.cfg.model}</Meta>
                        <Meta>IP <Mono>{p.cfg.ip}</Mono></Meta>
                        <Meta title={p.cfg.sn}>S/N <Mono>{short(p.cfg.sn, 8)}</Mono></Meta>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* PCS → своя група стійок */}
              <div style={{ position: 'relative', height: 12 }}>
                <VLine left="25%" h={12} color={lineC(bk.pcs[0]?.state ?? 'nodata')} />
                <VLine left="75%" h={12} color={lineC(bk.pcs[1]?.state ?? 'nodata')} />
              </div>

              <div {...bind(`bank:${bk.cfg.pos}`)} tabIndex={0} role="button"
                style={{ ...tile, padding: 0, cursor: 'pointer', outline: 'none', overflow: 'hidden' }}>
                <div style={{ padding: '7px 9px 6px', display: 'grid', gap: 1 }}>
                  <span className="flex items-center justify-between" style={{ gap: 6 }}>
                    <span className="font-display" style={{ fontSize: 12.5, fontWeight: 700, color: scada.text }}>
                      {bk.cfg.pos} <span style={{ fontSize: 10.5, fontWeight: 500, color: scada.textMuted }}>{drawing.vendors.bank} {bk.cfg.model}</span>
                    </span>
                    <span className="font-display tabular-nums" style={{ fontSize: 13, fontWeight: 700, color: scada.text }}>{ok(bk.bat?.socPct) ? `${nf0.format(bk.bat.socPct)} %` : '—'}</span>
                  </span>
                  <Meta>SCU <Mono>{bk.cfg.scu_ip}</Mono> · S/N <Mono>{bk.cfg.sn}</Mono> · {bk.cfg.racks} {i18n.t('ak_sld_racks')}</Meta>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', borderTop: `1px solid ${scada.border}` }}>
                  {bk.pcs.map((p, j) => (
                    <div key={p.cfg.pos} {...bind(`rg:${bk.cfg.pos}:${p.cfg.rack_group}`)} tabIndex={0} role="button"
                      style={{ padding: '6px 8px', borderLeft: j ? `1px solid ${scada.border}` : 'none', minWidth: 0, outline: 'none' }}>
                      <div className="flex items-center" style={{ gap: 5, fontSize: 11, fontWeight: 700, color: scada.text, whiteSpace: 'nowrap' }}>
                        <Dot c={CAT_META[CAT_OF[p.state]].color} size={6} hollow={p.state !== 'gen' && CAT_OF[p.state] === 'ok'} />{i18n.t('ak_sld_racks_2')} {p.cfg.racks}
                      </div>
                      <Meta>Group {p.cfg.rack_group} · {i18n.t('ak_sld_from_pcs')} {p.cfg.pos}</Meta>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── блок КТП: КТП → EzLogger → групи D1/D2 → інвертори ─────────────────────

interface InvView { cfg: TopoInverter; live?: InvLatest; state: NodeState; reason: Reason | null }
interface GroupView { cfg: TopoGroup; inv: InvView[]; range: string; breakers: string; pac: number; online: number; state: NodeState }
interface KtpView {
  cfg: TopoKtp; groups: GroupView[]; count: number; pac: number; online: number; state: NodeState; at: string | null; hz: number | null;
  issues: Issue[]; summary: { text: string; cat: Cat } | null;
}

function KtpBlock({ k, drawing, nominalKw, bind, active, onOpen }: {
  k: KtpView; drawing: StationDrawing; nominalKw: number; bind: Bind; active: boolean; onOpen: () => void;
}) {
  const mvs = drawing.ktp[k.cfg.pos];
  const nominal = k.count * nominalKw;
  const lineC = (s: NodeState) => (CAT_OF[s] === 'ok' ? (s === 'gen' ? LINE_OK : LINE_IDLE) : CAT_META[CAT_OF[s]].color);
  const model = k.groups[0]?.inv[0]?.cfg.model ?? '';
  return (
    <div style={{ minWidth: 0, display: 'grid', alignContent: 'start', position: 'relative', zIndex: 1 }}>
      <button type="button" data-sld-anchor={`ktp:${k.cfg.pos}`} {...bind(`ktp:${k.cfg.pos}`, { onClick: onOpen })}
        style={{ ...tile, padding: '10px 12px', textAlign: 'left', cursor: 'pointer', display: 'grid', gap: 6,
                 borderColor: active ? scada.borderStrong : scada.border, boxShadow: active ? `0 0 0 1px ${scada.borderStrong}, var(--shadow)` : 'var(--shadow)' }}>
        <span className="flex items-center justify-between" style={{ gap: 8 }}>
          <span className="font-display" style={{ fontSize: 15, fontWeight: 700, color: scada.text }}>
            {cx(k.cfg.pos)} <span style={{ fontSize: 11, fontWeight: 600, color: scada.textMuted }}>· {i18n.t('ak_sld_transformer')} {k.cfg.transformer}</span>
          </span>
          <StatePill cat={CAT_OF[k.state]} />
        </span>
        <ReasonLine s={k.summary} />
        <span style={{ display: 'grid' }}>
          {mvs && <Meta>{drawing.vendors.pv} {mvs.mvs} · {cx(mvs.rating)}</Meta>}
          <Meta>EzLogger {cx(k.cfg.pos)} · IP <Mono>{k.cfg.ezlogger.ip}</Mono> · S/N <Mono>{k.cfg.ezlogger.sn}</Mono></Meta>
          <Meta>{i18n.t('ak_sld_inverters', { n: k.count, v: drawing.vendors.pv, m: model, r: cx(drawing.ratings.inverter) })}</Meta>
        </span>
        <span style={{ display: 'grid', gap: 5, borderTop: `1px solid ${scada.border}`, paddingTop: 6 }}>
          <span className="flex items-baseline justify-between" style={{ gap: 10 }}>
            <span className="font-display tabular-nums" style={{ fontSize: 18, fontWeight: 700, color: scada.text }}>
              {k.state === 'nodata' ? '—' : mwOrKw(k.pac)}
            </span>
            <span className="tabular-nums" style={{ fontSize: 11, color: scada.textDim }}>{i18n.t('ak_sld_online_2')} {k.online}/{k.count}</span>
          </span>
          <span style={{ display: 'block', height: 3, borderRadius: 999, background: scada.panel2, overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${Math.min(100, (k.pac / Math.max(1, nominal)) * 100)}%`, background: scada.state.ok }} />
          </span>
        </span>
      </button>

      {/* КТП → дві групи */}
      <div style={{ position: 'relative', height: 22 }}>
        <VLine left="50%" h={11} color={lineC(k.state)} />
        <span aria-hidden="true" style={{ position: 'absolute', left: '25%', right: '25%', top: 10, height: 2, background: lineC(k.state) }} />
        {k.groups.slice(0, 2).map((g, i) => <VLine key={g.cfg.group} left={i ? '75%' : '25%'} top={10} h={12} color={lineC(g.state)} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, k.groups.length)}, minmax(0, 1fr))` }}>
        {k.groups.map((g) => (
          <div key={g.cfg.group} style={{ padding: '0 4px', minWidth: 0 }}>
            <div style={{ ...tile, padding: '8px 9px', display: 'grid', gap: 7 }}>
              <div {...bind(`grp:${k.cfg.pos}:${g.cfg.group}`)} tabIndex={0} role="button" style={{ display: 'grid', gap: 1, cursor: 'pointer', outline: 'none' }}>
                <span className="flex items-center justify-between" style={{ gap: 6 }}>
                  <span className="font-display" style={{ fontSize: 12.5, fontWeight: 700, color: scada.text, whiteSpace: 'nowrap' }}>{i18n.t('ak_sld_group_3')} {g.cfg.group} · {g.range}</span>
                  <Dot c={CAT_META[CAT_OF[g.state]].color} size={8} hollow={g.state === 'idle'} />
                </span>
                <Meta wrap title={i18n.t('ak_sld_logger_plc_channel_plc_channel_number', { c: g.cfg.channel, p: g.cfg.plc })}>{i18n.t('ak_sld_channel')} {g.cfg.channel} · {g.cfg.plc}</Meta>
                <Meta wrap>{i18n.t('ak_sld_breakers')} {g.breakers}</Meta>
                <span className="flex items-baseline justify-between tabular-nums" style={{ fontSize: 11, color: scada.textDim, marginTop: 2 }}>
                  <span><b style={{ color: scada.text, fontWeight: 700 }}>{kw(g.pac)}</b> {i18n.t('ak_sld_kw')}</span>
                  <span>{g.online}/{g.inv.length}</span>
                </span>
              </div>
              <div style={{ display: 'grid', gap: 4, gridTemplateColumns: 'repeat(auto-fill, minmax(42px, 1fr))' }}>
                {g.inv.map((x) => <InvChip key={x.cfg.unit_id} x={x} nominalKw={nominalKw} bind={bind} />)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InvChip({ x, nominalKw, bind }: { x: InvView; nominalKw: number; bind: Bind }) {
  const cat = CAT_OF[x.state];
  const c = CAT_META[cat].color;
  const attention = cat === 'warn' || cat === 'alarm';
  const pct = x.state !== 'nodata' && ok(x.live?.pacKw) ? (x.live.pacKw / nominalKw) * 100 : 0;
  return (
    <button type="button" {...bind(`inv:${x.cfg.unit_id}`)} aria-label={i18n.t('ak_sld_inverter', { p: x.cfg.pos, s: INV_LABEL[x.state] })}
      style={{
        position: 'relative', padding: '5px 2px 8px', borderRadius: 7, cursor: 'pointer', textAlign: 'center', minWidth: 0,
        background: attention ? `color-mix(in srgb, ${c} 14%, transparent)` : 'transparent',
        border: `1px ${cat === 'nodata' ? 'dashed' : 'solid'} ${attention ? `color-mix(in srgb, ${c} 65%, transparent)` : scada.border}`,
      }}>
      <span className="font-display tabular-nums" style={{ fontSize: 11.5, fontWeight: 700, color: cat === 'nodata' ? scada.textMuted : attention ? c : scada.text }}>{x.cfg.pos}</span>
      <span aria-hidden="true" style={{ position: 'absolute', left: 5, right: 5, bottom: 3, height: 2.5, borderRadius: 999, background: scada.panel2, overflow: 'hidden' }}>
        <span style={{ display: 'block', height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`, background: x.state === 'gen' ? scada.state.ok : c }} />
      </span>
    </button>
  );
}

